/**
 * `ErrorState` - in-page, retryable failure panel for the list view.
 *
 * Per 04b Section 5 + 04a primitives table: `role="alert"` (announces
 * immediately). Always surfaces the correlation id as selectable text so
 * the user can paste it to ops. The "Try again" button calls the supplied
 * `onRetry` (typically TanStack Query's `refetch`).
 *
 * Distinct from `pages/error.tsx`, which is the full-page navigation
 * error route.
 */

import { Button } from './button'

export interface ErrorStateProps {
  readonly title?: string
  readonly description?: string
  readonly correlationId: string | null
  readonly onRetry: () => void
}

export function ErrorState({
  title = 'Something went wrong loading members.',
  description = 'If this keeps happening, give the correlation id to the on-call ops engineer.',
  correlationId,
  onRetry,
}: ErrorStateProps): JSX.Element {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-murmuration-3 px-murmuration-6 py-murmuration-7 text-center"
    >
      <p className="text-murmuration-body-strong text-text-primary">{title}</p>
      {correlationId !== null && correlationId !== '' && (
        <p className="text-murmuration-body text-text-secondary">
          Correlation id:{' '}
          <code className="select-all font-mono text-murmuration-code text-text-primary">
            {correlationId}
          </code>
        </p>
      )}
      <Button variant="primary" onClick={onRetry} className="mt-murmuration-2">
        Try again
      </Button>
      <p className="mt-murmuration-2 text-murmuration-body text-text-muted">{description}</p>
    </div>
  )
}
